import './PermissionsFormModal.scss'

import ButtonSelect, {
  ButtonSelectItem
} from '../../../components/ButtonSelect/ButtonSelect'
import Checkbox, { CheckboxGroup } from '../../../components/Checkbox/Checkbox'
import { READY, useVisualState } from '../../../hooks/useVisualState'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import Button from '../../../components/Button/Button'
import { Form } from '../../../components/Form/Form'
import Modal from '../../../components/Modal/Modal'
import ModalContent from '../../../components/Modal/ModalContent'
import ModalFooter from '../../../components/Modal/ModalFooter'
import ModalHeader from '../../../components/Modal/ModalHeader'
import t from '../../../helpers/i18n'
import useForceUpdate from '../../../hooks/useForceUpdate'
import useObjectState from '../../../hooks/useObjectState'

const websiteFields = [
  'website_general',
  'website_home_page',
  'website_news',
  'website_documents',
  'website_contact'
]
const organizationFields = [
  'general_information',
  'members',
  'subscription_billing',
  'add_ons_store',
  ...websiteFields
]
const entitiesFields = [
  'clubs',
  'teams',
  'delegates',
  'players',
  'technical_staff'
]
const competitionsFields = ['competitions', 'divitions', 'categories']
const documentCenterFields = [
  'team_roster',
  'match_reports',
  'competition_statistics',
  'parental_control'
]

const PermissionsFormModal = ({ onCloseRequested }) => {
  const [[userState, user], setUser] = useVisualState()
  const [permissions, setPermissions] = useObjectState()
  const [formValid, setFormValid] = useState(false)
  const [, forceUpdate] = useForceUpdate()
  const userData = useRef()

  const handleSave = useCallback(() => {}, [])

  useEffect(() => {
    setUser(READY, {
      member_id: 1,
      name: 'Kaiya Bothman',
      email: 'kaiya.bothman@altium.mx',
      role: 'ADMIN'
    })
    setPermissions({
      general_information: 'view',
      members: 'view',
      website_general: 'view',
      website_home_page: 'view',
      website_news: 'view',
      website_documents: 'view',
      website_contact: 'view',
      subscription_billing: 'view',
      add_ons_store: 'view',
      clubs: 'view',
      teams: 'view',
      delegates: 'view',
      players: 'view',
      technical_staff: 'view',
      competitions: 'view',
      divitions: 'view',
      categories: 'view',
      team_roster: 'view',
      match_reports: 'view',
      competition_statistics: 'view',
      parental_control: 'view'
    })
  }, [setUser, setPermissions])

  const handleSelection = useCallback(
    (permission, name) => {
      setPermissions(name, permission)
    },
    [setPermissions]
  )

  const handleGroupSelection = useCallback(
    (permission, names) => {
      names.forEach((name) => {
        if (userData.current[name]) {
          setPermissions(name, permission)
        }
      })
    },
    [setPermissions]
  )

  return (
    <div className='permissions-form-modal'>
      {userState === READY && (
        <Modal onCloseRequested={onCloseRequested}>
          <ModalHeader title={t('titles.Access_for_%s', user.name)} back={false} />
          <Form
            data={userData}
            onValidityChange={setFormValid}
            onChange={forceUpdate}
          >
            <ModalContent>
              <CheckboxGroup group='organization'>
                <GroupSettingHelper
                  label={t('titles.Organization_Settings')}
                  monitor={organizationFields}
                  userData={userData}
                  permissions={permissions}
                  onSelected={handleGroupSelection}
                  highlighted
                />
                <Separator />
                <SettingHelper
                  name='general_information'
                  label={t('for_check.General_Information')}
                  group='organization'
                  userData={userData}
                  permissions={permissions}
                  onSelected={handleSelection}
                />
                <Separator />
                <SettingHelper
                  name='members'
                  label={t('for_check.Members')}
                  group='organization'
                  help='Members'
                  userData={userData}
                  permissions={permissions}
                  onSelected={handleSelection}
                />
                <Separator />
                <CheckboxGroup group='website'>
                  <GroupSettingHelper
                    label={t('titles.Public_Website')}
                    group='organization'
                    monitor={websiteFields}
                    userData={userData}
                    permissions={permissions}
                    onSelected={handleGroupSelection}
                  />
                  <Separator />
                  <Indent>
                    <SettingHelper
                      name='website_general'
                      label={t('for_check.General_Settings')}
                      group='website'
                      userData={userData}
                      permissions={permissions}
                      onSelected={handleSelection}
                    />
                    <Separator />
                    <SettingHelper
                      name='website_home_page'
                      label={t('for_check."Home Page"_section')}
                      group='website'
                      userData={userData}
                      permissions={permissions}
                      onSelected={handleSelection}
                    />
                    <Separator />
                    <SettingHelper
                      name='website_news'
                      label={t('for_check."News"_section')}
                      group='website'
                      userData={userData}
                      permissions={permissions}
                      onSelected={handleSelection}
                    />
                    <Separator />
                    <SettingHelper
                      name='website_documents'
                      label={t('for_check."Documents"_section')}
                      group='website'
                      userData={userData}
                      permissions={permissions}
                      onSelected={handleSelection}
                    />
                    <Separator />
                    <SettingHelper
                      name='website_contact'
                      label={t('for_check."Contact"_section')}
                      group='website'
                      userData={userData}
                      permissions={permissions}
                      onSelected={handleSelection}
                    />
                  </Indent>
                </CheckboxGroup>
                <Separator />
                <SettingHelper
                  name='subscription_billing'
                  label={t('for_check.Subscription_&_Billing')}
                  group='organization'
                  userData={userData}
                  permissions={permissions}
                  onSelected={handleSelection}
                />
                <Separator />
                <SettingHelper
                  name='add_ons_store'
                  label={t('for_check.Add_ons_Store')}
                  group='organization'
                  userData={userData}
                  permissions={permissions}
                  onSelected={handleSelection}
                />
              </CheckboxGroup>
            </ModalContent>

            <ModalContent>
              <CheckboxGroup group='entities'>
                <GroupSettingHelper
                  label={t('titles.Manage_Entities')}
                  monitor={entitiesFields}
                  userData={userData}
                  permissions={permissions}
                  onSelected={handleGroupSelection}
                  highlighted
                />
                <Separator />
                <SettingHelper
                  name='clubs'
                  label={t('for_check.Clubs')}
                  group='entities'
                  userData={userData}
                  permissions={permissions}
                  onSelected={handleSelection}
                />
                <Separator />
                <SettingHelper
                  name='teams'
                  label={t('for_check.Teams')}
                  group='entities'
                  userData={userData}
                  permissions={permissions}
                  onSelected={handleSelection}
                />
                <Separator />
                <SettingHelper
                  name='delegates'
                  label={t('for_check.Team_Delegates')}
                  group='entities'
                  userData={userData}
                  permissions={permissions}
                  onSelected={handleSelection}
                />
                <Separator />
                <SettingHelper
                  name='players'
                  label={t('for_check.Players')}
                  group='entities'
                  userData={userData}
                  permissions={permissions}
                  onSelected={handleSelection}
                />
                <Separator />
                <SettingHelper
                  name='technical_staff'
                  label={t('for_check.Technical_Staff')}
                  group='entities'
                  userData={userData}
                  permissions={permissions}
                  onSelected={handleSelection}
                />
              </CheckboxGroup>
            </ModalContent>

            <ModalContent>
              <CheckboxGroup group='competitions'>
                <GroupSettingHelper
                  label={t('titles.Manage_Competitions')}
                  monitor={competitionsFields}
                  userData={userData}
                  permissions={permissions}
                  onSelected={handleGroupSelection}
                  highlighted
                />
                <Separator />
                <SettingHelper
                  name='competitions'
                  label={t('for_check.Competitions')}
                  group='competitions'
                  userData={userData}
                  permissions={permissions}
                  onSelected={handleSelection}
                />
                <Separator />
                <SettingHelper
                  name='divitions'
                  label={t('for_check.Divisions')}
                  group='competitions'
                  userData={userData}
                  permissions={permissions}
                  onSelected={handleSelection}
                />
                <Separator />
                <SettingHelper
                  name='categories'
                  label={t('for_check.Player_Categories')}
                  group='competitions'
                  userData={userData}
                  permissions={permissions}
                  onSelected={handleSelection}
                />
              </CheckboxGroup>
            </ModalContent>

            <ModalContent>
              <CheckboxGroup group='document_center'>
                <GroupSettingHelper
                  label={t('titles.Document_Center')}
                  monitor={documentCenterFields}
                  userData={userData}
                  permissions={permissions}
                  onSelected={handleGroupSelection}
                  highlighted
                />
                <Separator />
                <SettingHelper
                  name='team_roster'
                  label={t('for_check.Team_Rosters')}
                  group='document_center'
                  userData={userData}
                  permissions={permissions}
                  onSelected={handleSelection}
                />
                <Separator />
                <SettingHelper
                  name='match_reports'
                  label={t('for_check.Match_Reports')}
                  group='document_center'
                  userData={userData}
                  permissions={permissions}
                  onSelected={handleSelection}
                />
                <Separator />
                <SettingHelper
                  name='competition_statistics'
                  label={t('for_check.Competition_Statistics')}
                  group='document_center'
                  userData={userData}
                  permissions={permissions}
                  onSelected={handleSelection}
                />
                <Separator />
                <SettingHelper
                  name='parental_control'
                  label={t('for_check.Parental_Control')}
                  group='document_center'
                  userData={userData}
                  permissions={permissions}
                  onSelected={handleSelection}
                />
              </CheckboxGroup>
            </ModalContent>
          </Form>
          <ModalFooter>
            <Button
              color='green'
              type='dark'
              disabled={!formValid}
              grow
              onClick={(event) => handleSave(event.currentTarget)}
              label={t('buttons.Save_access_settings')}
            />
          </ModalFooter>
        </Modal>
      )}
    </div>
  )
}

const SettingHelper = ({
  name,
  label,
  group,
  help,
  userData,
  permissions,
  onSelected
}) => {
  return (
    <div className="-row">
      <Checkbox name={name} label={label} group={group} help={help} grow />
      <ButtonSelect name={name} label={PERMISSIONS[permissions[name]] || PERMISSIONS['view']} size="small" onSelected={onSelected} disabled={!(userData.current && userData.current[name])}>
        {Object.keys(PERMISSIONS).map(key => (
          <ButtonSelectItem key={key} value={key} label={PERMISSIONS[key]} selected={key === permissions[name]} />
        ))}
      </ButtonSelect>
    </div>
  )
}

const GroupSettingHelper = ({
  label,
  group,
  monitor,
  highlighted,
  userData,
  permissions,
  onSelected
}) => {
  const [enabled, setEnabled] = useState()

  const handleSelection = useCallback(
    (permission) => {
      if (onSelected) onSelected(permission, monitor)
    },
    [onSelected, monitor]
  )

  const handleChange = useCallback((checked) => {
    setEnabled(checked)
  }, [])

  const selections = monitor.reduce((memo, key) => {
    if (userData.current[key]) memo[permissions[key]] = true
    return memo
  }, {})
  const keys = Object.keys(selections)
  const isMixed = keys.length > 1
  const selection = keys[0] || 'view'

  return (
    <div className="-row">
      <Checkbox label={label} group={group} highlighted={highlighted} grow manager onChange={handleChange} />
      <ButtonSelect label={isMixed ? 'Mixed' : PERMISSIONS[selection]} size="small" onSelected={handleSelection} disabled={!enabled}>
        {Object.keys(PERMISSIONS).map(key => (
          <ButtonSelectItem key={key} value={key} label={PERMISSIONS[key]} selected={!isMixed && key === selection} />
        ))}
      </ButtonSelect>
    </div>
  )
}

const PERMISSIONS = {
  view: t('menu.Can_view'),
  edit: t('menu.Can_edit'),
  delete: t('menu.Can_delete')
}

const Separator = () => <div className='-form-line-separator'></div>

const Indent = ({ children }) => <div className='-form-indent'>{children}</div>

export default PermissionsFormModal
